import React, { useState, useRef, useCallback, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';
import axios from 'axios';
import * as XLSX from 'xlsx';
import BuyNumberModal from '../../settings/components/BuyNumberModal';

// ─── Constants ────────────────────────────────────────────────────────────────
const CAMPAIGN_TYPES = [
  'Lead Qualification', 'Cart Abandonment', 'Payment Reminder', 'Order Confirmation',
  'NPS Survey', 'Collections', 'Appointment Booking', 'Address Confirmation',
  'Re-engagement', 'Win-back', 'Custom',
];

const COUNTRY_CODES = [
  { flag: '\u{1F1EE}\u{1F1F3}', name: 'India', code: '+91' },
  { flag: '\u{1F1FA}\u{1F1F8}', name: 'United States', code: '+1' },
  { flag: '\u{1F1EC}\u{1F1E7}', name: 'United Kingdom', code: '+44' },
  { flag: '\u{1F1E6}\u{1F1FA}', name: 'Australia', code: '+61' },
  { flag: '\u{1F1F8}\u{1F1EC}', name: 'Singapore', code: '+65' },
];

const TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'IST (UTC +5:30)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'EST (UTC -5:00)' },
  { value: 'America/Los_Angeles', label: 'PST (UTC -8:00)' },
  { value: 'Europe/London', label: 'GMT (UTC +0:00)' },
  { value: 'America/Chicago', label: 'CST (UTC -6:00)' },
];

const RETRY_OPTIONS = ['1', '2', '3', '5'];
const COOLDOWN_OPTIONS = [
  { label: '15 mins', value: 15 },
  { label: '30 mins', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '3 hours', value: 180 },
  { label: '24 hours', value: 1440 },
];
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1)?.padStart(2, '0'));
const MINUTES_LIST = ['00', '15', '30', '45'];

const AVATAR_COLORS = [
  'bg-purple-100 text-purple-700',
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-orange-100 text-orange-700',
  'bg-pink-100 text-pink-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
  'bg-rose-100 text-rose-700',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
};

const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

const parseCSVPreview = (file, maxRows = 50) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length === 0) { resolve({ headers: [], rows: [] }); return; }
        const headers = parseCSVLine(lines[0]);
        const rows = lines.slice(1, maxRows + 1).map((line, idx) => {
          const values = parseCSVLine(line);
          const row = { _rowIndex: idx + 1 };
          headers.forEach((h, i) => { row[h] = values[i] || ''; });
          return row;
        });
        resolve({ headers, rows });
      } catch {
        resolve({ headers: [], rows: [] });
      }
    };
    // Read only first 64KB — enough for header + ~50 rows
    const chunk = file.slice(0, 64 * 1024);
    reader.readAsText(chunk);
  });
};

const parseXLSXPreview = (file, maxRows = 50) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', sheetRows: maxRows + 1 });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        if (json.length === 0) { resolve({ headers: [], rows: [] }); return; }
        const headers = Object.keys(json[0]);
        const rows = json.slice(0, maxRows).map((row, idx) => ({ _rowIndex: idx + 1, ...row }));
        resolve({ headers, rows });
      } catch {
        resolve({ headers: [], rows: [] });
      }
    };
    reader.readAsArrayBuffer(file);
  });
};

const convertTo24Hour = (hour, minute, ampm) => {
  let h = parseInt(hour, 10);
  if (ampm === 'AM' && h === 12) h = 0;
  if (ampm === 'PM' && h !== 12) h += 12;
  return `${String(h).padStart(2, '0')}:${minute}`;
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center gap-0 mb-8">
    {[{ num: 1, label: 'Upload Details' }, { num: 2, label: 'Configure Campaign' }]?.map((step, idx) => (
      <React.Fragment key={step?.num}>
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
            currentStep === step?.num
              ? 'bg-[#2F55FF] text-white shadow-md shadow-blue-200'
              : currentStep > step?.num
              ? 'bg-[#2F55FF] text-white'
              : 'border-2 border-gray-300 text-gray-400 bg-white'
          }`}>
            {currentStep > step?.num ? <Icon name="Check" size={13} /> : step?.num}
          </div>
          <span className={`text-sm font-medium ${currentStep === step?.num ? 'text-[#2F55FF]' : currentStep > step?.num ? 'text-gray-700' : 'text-gray-400'}`}>
            {step?.label}
          </span>
        </div>
        {idx < 1 && (
          <div className={`flex-1 h-px mx-4 min-w-[48px] transition-colors ${currentStep > 1 ? 'bg-[#2F55FF]' : 'bg-gray-200'}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const SectionCard = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-6 mb-6">
    <div className="mb-5">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const FieldLabel = ({ children, required = false }) => (
  <label className="block text-sm font-medium text-gray-700 mb-1.5">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const InputField = ({ placeholder, value, onChange, type = 'text', className = '' }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    className={`w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 focus:border-[#2F55FF] bg-white text-gray-900 placeholder-gray-400 transition-all ${className}`}
  />
);

const SelectField = ({ value, onChange, children, className = '' }) => (
  <select
    value={value}
    onChange={onChange}
    className={`w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 focus:border-[#2F55FF] bg-white text-gray-900 transition-all appearance-none ${className}`}
  >
    {children}
  </select>
);

const Toggle = ({ checked, onChange, label, description }) => (
  <div className="flex items-start gap-3 py-3">
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none mt-0.5 ${checked ? 'bg-[#2F55FF]' : 'bg-gray-200'}`}
    >
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
    <div>
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
  </div>
);

// ─── CSV Preview Panel ──────────────────────────────────────────────────────────
const CSVPreviewPanel = ({ uploadedFile, csvPreview, onChangeFile }) => {
  const headers = csvPreview?.headers || [];
  const rows = csvPreview?.rows || [];

  if (!uploadedFile || headers.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm flex flex-col items-center justify-center h-full" style={{ minHeight: 600 }}>
        <div className="text-center px-8">
          <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
            <Icon name="FileText" size={28} className="text-gray-300" />
          </div>
          <p className="text-sm font-medium text-gray-500 mb-1">No file uploaded yet</p>
          <p className="text-xs text-gray-400">Upload a CSV or Excel file to preview your calling list here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm flex flex-col h-full" style={{ minHeight: 600 }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
            <Icon name="FileText" size={18} className="text-[#2F55FF]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{uploadedFile?.name}</p>
            <p className="text-xs text-gray-500">{formatFileSize(uploadedFile?.size)}</p>
          </div>
        </div>
        <button
          onClick={onChangeFile}
          className="text-xs text-[#2F55FF] font-medium px-3 py-1.5 rounded-lg border border-[#2F55FF]/30 hover:bg-blue-50 transition-colors"
        >
          Change File
        </button>
      </div>
      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gray-50/90 backdrop-blur-sm z-10">
            <tr className="border-b border-[#E5E7EB]">
              <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">#</th>
              {headers?.map(col => (
                <th key={col} className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows?.map((row, i) => (
              <tr key={i} className="border-b border-[#F3F4F6] hover:bg-blue-50/30 transition-colors">
                <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap">{row._rowIndex}</td>
                {headers?.map(col => (
                  <td key={col} className="px-4 py-2.5 text-gray-600 whitespace-nowrap max-w-[200px] truncate">{row[col]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Footer */}
      <div className="px-5 py-3.5 border-t border-[#E5E7EB] bg-gray-50/50 rounded-b-2xl flex items-center gap-6">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">Columns</span>
          <span className="text-xs font-semibold text-gray-800">{headers.length}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">Preview Rows</span>
          <span className="text-xs font-semibold text-gray-800">{rows.length}</span>
        </div>
      </div>
    </div>
  );
};

// ─── Step 1 Left Panel ──────────────────────────────────────────────────────────
const Step1Panel = ({
  form, setForm, uploadedFile, setUploadedFile, isDragging, setIsDragging,
  fileInputRef, agents, loadingAgents, audiences, loadingAudiences,
  csvPreview, callingListTab, setCallingListTab, mappings, setMappings,
  uploading,
}) => {
  const [assistantSearch, setAssistantSearch] = useState('');
  const [assistantDropdownOpen, setAssistantDropdownOpen] = useState(false);
  const [audienceSearch, setAudienceSearch] = useState('');
  const assistantRef = useRef(null);

  const csvHeaders = csvPreview?.headers || [];

  const filteredAssistants = agents?.filter(a =>
    a?.name?.toLowerCase()?.includes(assistantSearch?.toLowerCase())
  );

  const selectedAgent = agents?.find(a => a?.id === form?.assistant);

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) setUploadedFile(file);
  }, [setIsDragging, setUploadedFile]);

  const handleDragOver = (e) => { e?.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleFileInput = (e) => {
    const file = e?.target?.files?.[0];
    if (file) setUploadedFile(file);
  };

  return (
    <div>
      {/* Section 1: Campaign & Assistant */}
      <SectionCard title="Campaign & Assistant" subtitle="Give your campaign a name and assign an AI assistant.">
        <div className="space-y-4">
          <div>
            <FieldLabel required>Campaign Name</FieldLabel>
            <InputField
              placeholder="e.g. Q3 Lead Nurture - Enterprise"
              value={form?.campaignName}
              onChange={e => setForm(f => ({ ...f, campaignName: e?.target?.value }))}
            />
          </div>
          <div>
            <FieldLabel required>Campaign Type</FieldLabel>
            <div className="relative">
              <SelectField
                value={form?.campaignType}
                onChange={e => setForm(f => ({ ...f, campaignType: e?.target?.value }))}
              >
                <option value="">Select campaign type</option>
                {CAMPAIGN_TYPES?.map(t => (
                  <option key={t} disabled={t !== 'Lead Qualification'} style={t !== 'Lead Qualification' ? { color: '#9CA3AF' } : {}}>
                    {t}
                  </option>
                ))}
              </SelectField>
              <Icon name="ChevronDown" size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <FieldLabel required>Select AI Assistant</FieldLabel>
            <div className="relative" ref={assistantRef}>
              <div
                className="w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-xl focus-within:ring-2 focus-within:ring-[#2F55FF]/20 focus-within:border-[#2F55FF] bg-white cursor-pointer flex items-center justify-between"
                onClick={() => setAssistantDropdownOpen(v => !v)}
              >
                {selectedAgent ? (
                  <div className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${AVATAR_COLORS[agents?.indexOf(selectedAgent) % AVATAR_COLORS.length]}`}>
                      {selectedAgent?.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedAgent?.name}</p>
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-400">{loadingAgents ? 'Loading assistants...' : 'Search assistants...'}</span>
                )}
                <Icon name="ChevronDown" size={15} className="text-gray-400" />
              </div>
              {assistantDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-xl z-40">
                  <div className="p-2 border-b border-[#E5E7EB]">
                    <div className="relative">
                      <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search assistants..."
                        value={assistantSearch}
                        onChange={e => setAssistantSearch(e?.target?.value)}
                        className="w-full pl-8 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20"
                        onClick={e => e?.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="max-h-52 overflow-y-auto py-1">
                    {loadingAgents ? (
                      <div className="px-4 py-6 text-center text-sm text-gray-400">Loading...</div>
                    ) : filteredAssistants?.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-gray-400">No assistants found</div>
                    ) : (
                      filteredAssistants?.map((a, idx) => (
                        <button
                          key={a?.id}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                          onClick={() => { setForm(f => ({ ...f, assistant: a?.id })); setAssistantDropdownOpen(false); setAssistantSearch(''); }}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}`}>
                            {a?.name?.[0]?.toUpperCase()}
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-900">{a?.name}</p>
                          </div>
                          {form?.assistant === a?.id && <Icon name="Check" size={14} className="text-[#2F55FF] ml-auto" />}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Section 2: Upload Calling List */}
      <SectionCard title="Upload Calling List" subtitle="Upload your customer CSV or Excel file, or select from an existing audience.">
        {/* Tab Switcher */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-5">
          <button
            type="button"
            onClick={() => setCallingListTab('upload')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${callingListTab === 'upload' ? 'bg-white text-[#2F55FF] shadow-sm border border-[#E5E7EB]' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Icon name="Upload" size={14} />
            Upload File
          </button>
          <button
            type="button"
            onClick={() => setCallingListTab('audience')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${callingListTab === 'audience' ? 'bg-white text-[#2F55FF] shadow-sm border border-[#E5E7EB]' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Icon name="Users" size={14} />
            Select Audience
          </button>
        </div>

        {callingListTab === 'upload' ? (
          <>
            {uploading ? (
              <div className="border-2 border-dashed border-blue-300 bg-blue-50 rounded-xl p-10 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                  <p className="text-sm font-medium text-blue-700">Uploading file...</p>
                </div>
              </div>
            ) : !uploadedFile ? (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer ${isDragging ? 'border-[#2F55FF] bg-blue-50/50' : 'border-[#E5E7EB] hover:border-[#2F55FF]/50 hover:bg-gray-50/50'}`}
                onClick={() => fileInputRef?.current?.click()}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                  <Icon name="Upload" size={22} className="text-[#2F55FF]" />
                </div>
                <p className="text-sm font-semibold text-gray-800 mb-1">Drag & drop your file here</p>
                <p className="text-xs text-gray-500 mb-4">Supported formats: CSV, XLSX &middot; Max size: 100 MB</p>
                <button
                  type="button"
                  className="px-5 py-2.5 bg-[#2F55FF] text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
                  onClick={e => { e?.stopPropagation(); fileInputRef?.current?.click(); }}
                >
                  Browse Files
                </button>
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleFileInput} />
              </div>
            ) : (
              <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="FileText" size={20} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{uploadedFile?.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(uploadedFile?.size)} &middot; Upload successful</p>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" size={18} className="text-green-500" />
                  <button
                    onClick={() => { setUploadedFile(null); if (fileInputRef?.current) fileInputRef.current.value = ''; }}
                    className="text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    Replace File
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            {/* Search */}
            <div className="relative mb-3">
              <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search audiences..."
                value={audienceSearch}
                onChange={e => setAudienceSearch(e?.target?.value)}
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 focus:border-[#2F55FF] bg-white text-gray-900 placeholder-gray-400"
              />
            </div>
            {/* Audience List */}
            <div className="space-y-2 max-h-64 overflow-y-auto pr-0.5">
              {loadingAudiences ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-sm">Loading audiences...</p>
                </div>
              ) : audiences?.filter(a =>
                a?.name?.toLowerCase()?.includes(audienceSearch?.toLowerCase())
              )?.map(audience => (
                <button
                  key={audience?.id}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, selectedAudience: f?.selectedAudience === audience?.id ? null : audience?.id }))}
                  className={`w-full text-left flex items-center gap-3 p-3.5 rounded-xl border transition-all ${
                    form?.selectedAudience === audience?.id
                      ? 'border-[#2F55FF] bg-blue-50/60 shadow-sm'
                      : 'border-[#E5E7EB] hover:border-[#2F55FF]/40 hover:bg-gray-50/60'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${form?.selectedAudience === audience?.id ? 'bg-[#2F55FF]/10' : 'bg-gray-100'}`}>
                    <Icon name="Users" size={16} className={form?.selectedAudience === audience?.id ? 'text-[#2F55FF]' : 'text-gray-500'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-semibold text-gray-900 truncate">{audience?.name}</p>
                      {form?.selectedAudience === audience?.id && (
                        <Icon name="CheckCircle" size={14} className="text-[#2F55FF] flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">{audience?.totalRows?.toLocaleString()} rows</span>
                      <span className="text-xs text-green-600">{audience?.validCount?.toLocaleString()} valid</span>
                      <span className="text-xs text-gray-400">&middot; {audience?.source}</span>
                    </div>
                  </div>
                </button>
              ))}
              {!loadingAudiences && audiences?.filter(a =>
                a?.name?.toLowerCase()?.includes(audienceSearch?.toLowerCase())
              )?.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <Icon name="SearchX" size={24} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No audiences found</p>
                </div>
              )}
            </div>
            {form?.selectedAudience && (
              <div className="mt-3 pt-3 border-t border-[#E5E7EB] flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  <span className="font-medium text-[#2F55FF]">{audiences?.find(a => a?.id === form?.selectedAudience)?.name}</span> selected
                </p>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, selectedAudience: null }))}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Section 3: Calling List Settings */}
      <SectionCard title="Calling List Settings" subtitle="">
        <div className="space-y-5">
          <div>
            <FieldLabel required>Country Code</FieldLabel>
            <div className="relative">
              <SelectField
                value={form?.countryCode}
                onChange={e => setForm(f => ({ ...f, countryCode: e?.target?.value }))}
              >
                {COUNTRY_CODES?.map(c => (
                  <option key={c?.code} value={c?.code} disabled={c?.code !== '+91'} style={c?.code !== '+91' ? { color: '#9CA3AF' } : {}}>
                    {c?.flag} {c?.name} ({c?.code}){c?.code !== '+91' ? ' (Coming Soon)' : ''}
                  </option>
                ))}
              </SelectField>
              <Icon name="ChevronDown" size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Variable Mapping — only show when CSV headers are available */}
          {csvHeaders.length > 0 && callingListTab === 'upload' && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Map Variables to CSV Columns</p>
              <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-[#E5E7EB]">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Calltify Variable</th>
                      <th className="px-3 py-2.5 text-gray-300 text-center">&rarr;</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">CSV Column</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings?.map((row, i) => (
                      <tr key={i} className="border-b border-[#F3F4F6] last:border-0">
                        <td className="px-4 py-2.5 text-gray-700 text-xs font-medium">{row?.variable}</td>
                        <td className="px-3 py-2.5 text-center text-gray-300 text-xs">&rarr;</td>
                        <td className="px-4 py-2.5">
                          <select
                            value={row?.csvColumn}
                            onChange={e => setMappings(m => m?.map((r, idx) => idx === i ? { ...r, csvColumn: e?.target?.value } : r))}
                            className="w-full text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2F55FF]/20 bg-white text-gray-700"
                          >
                            <option value="">-- Select column --</option>
                            {csvHeaders?.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Toggles */}
          <div className="border-t border-[#E5E7EB] pt-4 space-y-1">
            <Toggle
              checked={form?.removeInvalid}
              onChange={v => setForm(f => ({ ...f, removeInvalid: v }))}
              label="Remove Invalid Rows"
              description="Automatically skip invalid phone numbers."
            />
            <Toggle
              checked={form?.transliterate}
              onChange={v => setForm(f => ({ ...f, transliterate: v }))}
              label="Transliterate Customer Names"
              description="Automatically convert names into the AI assistant's speaking language."
            />
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

// ─── Step 2 Left Panel ──────────────────────────────────────────────────────────
const Step2Panel = ({ form, setForm, phoneNumbers, loadingNumbers, onBuyNumber }) => {
  const [notifEmail, setNotifEmail] = useState('');

  const addNotifEmail = () => {
    const email = notifEmail.trim();
    if (email && email.includes('@') && !form?.notifEmails?.includes(email)) {
      setForm(f => ({ ...f, notifEmails: [...(f?.notifEmails || []), email] }));
      setNotifEmail('');
    }
  };

  const removeNotifEmail = (email) => {
    setForm(f => ({ ...f, notifEmails: f?.notifEmails?.filter(e => e !== email) }));
  };

  const activeNumbers = phoneNumbers?.filter(n => n?.status?.toUpperCase() === 'ACTIVE') || [];

  return (
    <div>
      {/* Section 1: Select Calling Number */}
      <SectionCard title="Select Number" subtitle="Choose the phone number customers will receive calls from.">
        <div className="space-y-4">
          <div>
            <FieldLabel required>Calling Number</FieldLabel>
            <div className="relative">
              <SelectField
                value={form?.callingNumber}
                onChange={e => setForm(f => ({ ...f, callingNumber: e?.target?.value }))}
              >
                <option value="">{loadingNumbers ? 'Loading numbers...' : 'Select a number'}</option>
                {activeNumbers.map(n => (
                  <option key={n?.e164} value={n?.e164}>{n?.e164}</option>
                ))}
              </SelectField>
              <Icon name="ChevronDown" size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            {activeNumbers.length === 0 && !loadingNumbers && (
              <p className="text-xs text-gray-500 mt-1.5">
                No phone numbers available.{' '}
                <button type="button" onClick={onBuyNumber} className="text-[#2F55FF] hover:underline font-medium">Buy a Number</button>
              </p>
            )}
            {activeNumbers.length > 0 && (
              <p className="text-xs text-gray-500 mt-1.5">
                Need more numbers?{' '}
                <button type="button" onClick={onBuyNumber} className="text-[#2F55FF] hover:underline font-medium">Buy New Number</button>
              </p>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Section 2: Campaign Settings */}
      <SectionCard title="Campaign Settings" subtitle="">
        <div className="space-y-5">
          {/* Campaign Duration */}
          <div>
            <FieldLabel required>Campaign Duration</FieldLabel>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Start Date & Time</p>
                <InputField
                  type="datetime-local"
                  value={form?.startDate}
                  onChange={e => setForm(f => ({ ...f, startDate: e?.target?.value }))}
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1.5">End Date & Time</p>
                <InputField
                  type="datetime-local"
                  value={form?.endDate}
                  onChange={e => setForm(f => ({ ...f, endDate: e?.target?.value }))}
                />
              </div>
            </div>
          </div>

          {/* Calling Window */}
          <div>
            <FieldLabel>Calling Window</FieldLabel>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 w-6">From</span>
              <div className="flex items-center gap-1.5">
                <select value={form?.windowFromHour} onChange={e => setForm(f => ({ ...f, windowFromHour: e?.target?.value }))}
                  className="py-2 pl-3 pr-8 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 bg-white text-gray-900">
                  {HOURS?.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <span className="text-gray-400 text-sm">:</span>
                <select value={form?.windowFromMin} onChange={e => setForm(f => ({ ...f, windowFromMin: e?.target?.value }))}
                  className="py-2 pl-3 pr-8 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 bg-white text-gray-900">
                  {MINUTES_LIST?.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={form?.windowFromAmPm} onChange={e => setForm(f => ({ ...f, windowFromAmPm: e?.target?.value }))}
                  className="py-2 pl-3 pr-8 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 bg-white text-gray-900">
                  <option value="AM">AM</option><option value="PM">PM</option>
                </select>
              </div>
              <span className="text-xs text-gray-500 w-4">To</span>
              <div className="flex items-center gap-1.5">
                <select value={form?.windowToHour} onChange={e => setForm(f => ({ ...f, windowToHour: e?.target?.value }))}
                  className="py-2 pl-3 pr-8 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 bg-white text-gray-900">
                  {HOURS?.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <span className="text-gray-400 text-sm">:</span>
                <select value={form?.windowToMin} onChange={e => setForm(f => ({ ...f, windowToMin: e?.target?.value }))}
                  className="py-2 pl-3 pr-8 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 bg-white text-gray-900">
                  {MINUTES_LIST?.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={form?.windowToAmPm} onChange={e => setForm(f => ({ ...f, windowToAmPm: e?.target?.value }))}
                  className="py-2 pl-3 pr-8 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 bg-white text-gray-900">
                  <option value="AM">AM</option><option value="PM">PM</option>
                </select>
              </div>
            </div>
          </div>

          {/* Retry & Cooldown */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel>Retry Count</FieldLabel>
              <div className="relative">
                <SelectField value={form?.retryCount} onChange={e => setForm(f => ({ ...f, retryCount: e?.target?.value }))}>
                  {RETRY_OPTIONS?.map(r => <option key={r}>{r}</option>)}
                </SelectField>
                <Icon name="ChevronDown" size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div>
              <FieldLabel>Cooldown Period</FieldLabel>
              <div className="relative">
                <SelectField value={form?.cooldown} onChange={e => setForm(f => ({ ...f, cooldown: e?.target?.value }))}>
                  {COOLDOWN_OPTIONS?.map(c => <option key={c?.value} value={c?.value}>{c?.label}</option>)}
                </SelectField>
                <Icon name="ChevronDown" size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Max Calls Per Hour & Day */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel>Max Calls / Hour</FieldLabel>
              <InputField
                type="number"
                min="1"
                placeholder="Unlimited"
                value={form?.maxCallsPerHour}
                onChange={e => setForm(f => ({ ...f, maxCallsPerHour: e?.target?.value }))}
              />
              <p className="text-xs text-gray-400 mt-1">Leave empty for no limit.</p>
            </div>
            <div>
              <FieldLabel>Max Calls / Day</FieldLabel>
              <InputField
                type="number"
                min="1"
                placeholder="Unlimited"
                value={form?.maxCallsPerDay}
                onChange={e => setForm(f => ({ ...f, maxCallsPerDay: e?.target?.value }))}
              />
              <p className="text-xs text-gray-400 mt-1">Leave empty for no limit.</p>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Section 3: Notifications */}
      <SectionCard title="Email Notifications" subtitle="Notify selected users when campaign starts, ends, or encounters failures.">
        <div>
          {/* Email tags */}
          {form?.notifEmails?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {form?.notifEmails?.map(email => (
                <span key={email} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-lg text-xs font-medium text-blue-700">
                  {email}
                  <button onClick={() => removeNotifEmail(email)} className="hover:text-blue-900">
                    <Icon name="X" size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="Enter email address..."
              value={notifEmail}
              onChange={e => setNotifEmail(e?.target?.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNotifEmail(); } }}
              className="flex-1 px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 focus:border-[#2F55FF] bg-white text-gray-900 placeholder-gray-400 transition-all"
            />
            <button
              type="button"
              onClick={addNotifEmail}
              className="px-4 py-2.5 text-sm font-medium bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Add
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1.5">Press Enter or click Add to add an email.</p>
        </div>
      </SectionCard>
    </div>
  );
};

// ─── Main Wizard ────────────────────────────────────────────────────────────────
const CampaignCreationWizard = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [callingListTab, setCallingListTab] = useState('upload');
  const fileInputRef = useRef(null);

  // API data
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [audiences, setAudiences] = useState([]);
  const [loadingAudiences, setLoadingAudiences] = useState(true);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loadingNumbers, setLoadingNumbers] = useState(true);
  const [showBuyNumberModal, setShowBuyNumberModal] = useState(false);

  // CSV
  const [csvPreview, setCsvPreview] = useState({ headers: [], rows: [] });
  const [fileKey, setFileKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Variable mappings
  const DEFAULT_MAPPINGS = [
    { variable: 'Customer Name', key: 'callee_name' },
    { variable: 'Phone Number', key: 'mobile_number' },
    { variable: 'Email', key: 'email' },
    { variable: 'Company Name', key: 'company_name' },
  ];
  const [mappings, setMappings] = useState(DEFAULT_MAPPINGS.map(m => ({ ...m, csvColumn: '' })));

  // Save/Launch state
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [savedCampaignId, setSavedCampaignId] = useState(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    campaignName: '',
    campaignType: '',
    assistant: null,
    countryCode: '+91',
    removeInvalid: true,
    transliterate: true,
    selectedAudience: null,
    callingNumber: '',
    timezone: 'Asia/Kolkata',
    startDate: '',
    endDate: '',
    windowFromHour: '09',
    windowFromMin: '00',
    windowFromAmPm: 'AM',
    windowToHour: '06',
    windowToMin: '00',
    windowToAmPm: 'PM',
    retryCount: '3',
    cooldown: '30',
    maxCallsPerHour: '',
    maxCallsPerDay: '',
    notifEmails: [],
  });

  // ─── Fetch agents ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoadingAgents(true);
        const res = await api.get('/api/v1/dashboard/get-agents');
        if (res.data?.success && res.data?.data?.agents) {
          const transformed = res.data.data.agents.map(a => ({
            id: a.agentId,
            name: a.agentName || 'Unnamed Agent',
          }));
          setAgents(transformed);
        }
      } catch (err) {
        console.error('Failed to fetch agents:', err);
      } finally {
        setLoadingAgents(false);
      }
    };
    fetchAgents();
  }, []);

  // ─── Fetch audiences (status=ready only) ────────────────────────────────
  useEffect(() => {
    const fetchAudiences = async () => {
      try {
        setLoadingAudiences(true);
        const res = await api.get('/api/v1/audience', { params: { page: 0, size: 100 } });
        const list = res.data?.data || [];
        setAudiences(list.filter(a => a.status === 'ready'));
      } catch (err) {
        console.error('Failed to fetch audiences:', err);
      } finally {
        setLoadingAudiences(false);
      }
    };
    fetchAudiences();
  }, []);

  // ─── Fetch owned phone numbers ──────────────────────────────────────────
  const fetchPhoneNumbers = useCallback(async () => {
    try {
      setLoadingNumbers(true);
      const res = await api.get('/api/v1/phone-numbers');
      const data = res.data?.data;
      setPhoneNumbers(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      console.error('Failed to fetch phone numbers:', err);
    } finally {
      setLoadingNumbers(false);
    }
  }, []);

  useEffect(() => {
    fetchPhoneNumbers();
  }, [fetchPhoneNumbers]);

  // ─── Handle file selection: parse preview + upload to S3 ────────────────
  const handleSetUploadedFile = useCallback(async (file) => {
    if (!file) {
      setUploadedFile(null);
      setCsvPreview({ headers: [], rows: [] });
      setFileKey(null);
      setUploadError('');
      setMappings(DEFAULT_MAPPINGS.map(m => ({ ...m, csvColumn: '' })));
      return;
    }

    // Validate file
    const validExts = ['.csv', '.xlsx'];
    const ext = file?.name?.slice(file?.name?.lastIndexOf('.'))?.toLowerCase();
    if (!validExts.includes(ext)) {
      setUploadError('Only CSV or Excel (.xlsx) files are supported.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setUploadError('File size must be under 100 MB.');
      return;
    }

    setUploadedFile(file);
    setUploadError('');

    // Parse preview
    if (ext === '.csv') {
      const preview = await parseCSVPreview(file);
      setCsvPreview(preview);

      // Auto-map common columns
      const headers = preview.headers.map(h => h.toLowerCase());
      setMappings(DEFAULT_MAPPINGS.map(m => {
        let matched = '';
        if (m.key === 'callee_name') {
          matched = preview.headers.find((_, i) => ['name', 'full_name', 'customer_name', 'full name', 'customer name'].includes(headers[i])) || '';
        } else if (m.key === 'mobile_number') {
          matched = preview.headers.find((_, i) => ['phone', 'mobile', 'phone_number', 'phone number', 'mobile_number', 'mobile number'].includes(headers[i])) || '';
        } else if (m.key === 'email') {
          matched = preview.headers.find((_, i) => ['email', 'email_address', 'email address'].includes(headers[i])) || '';
        } else if (m.key === 'company_name') {
          matched = preview.headers.find((_, i) => ['company', 'company_name', 'company name'].includes(headers[i])) || '';
        }
        return { ...m, csvColumn: matched };
      }));
    } else {
      const preview = await parseXLSXPreview(file);
      setCsvPreview(preview);
      const headers = preview.headers.map(h => String(h).toLowerCase());
      setMappings(DEFAULT_MAPPINGS.map(m => {
        let matched = '';
        if (m.key === 'callee_name') {
          matched = preview.headers.find((_, i) => ['name', 'full_name', 'customer_name', 'full name', 'customer name'].includes(headers[i])) || '';
        } else if (m.key === 'mobile_number') {
          matched = preview.headers.find((_, i) => ['phone', 'mobile', 'phone_number', 'phone number', 'mobile_number', 'mobile number'].includes(headers[i])) || '';
        } else if (m.key === 'email') {
          matched = preview.headers.find((_, i) => ['email', 'email_address', 'email address'].includes(headers[i])) || '';
        } else if (m.key === 'company_name') {
          matched = preview.headers.find((_, i) => ['company', 'company_name', 'company name'].includes(headers[i])) || '';
        }
        return { ...m, csvColumn: matched };
      }));
    }

    // Upload to S3 via presigned URL
    setUploading(true);
    try {
      const isXlsx = file.name?.toLowerCase().endsWith('.xlsx');
      const { data: presignRes } = await api.post(`/api/v1/campaigns/presigned-url${isXlsx ? '?fileName=' + encodeURIComponent(file.name) : ''}`);
      const { uploadUrl, fileKey: key } = presignRes?.data || {};

      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': isXlsx ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv' },
      });

      setFileKey(key);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError('Failed to upload file. Please try again.');
      setUploadedFile(null);
      setCsvPreview({ headers: [], rows: [] });
      setFileKey(null);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleChangeFile = () => {
    handleSetUploadedFile(null);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  // ─── Build API payload ──────────────────────────────────────────────────
  const buildPayload = () => {
    const sourceType = callingListTab === 'upload' ? 'csv_upload' : 'audience';

    // Build variable mapping object
    const variableMapping = {};
    mappings.forEach(m => {
      if (m.csvColumn) variableMapping[m.key] = m.csvColumn;
    });

    return {
      name: form.campaignName?.trim(),
      type: form.campaignType,
      agentId: form.assistant,
      sourceType,
      audienceId: sourceType === 'audience' ? form.selectedAudience : null,
      fileKey: sourceType === 'csv_upload' ? fileKey : null,
      variableMapping: Object.keys(variableMapping).length > 0 ? variableMapping : null,
      callingNumber: form.callingNumber?.trim() || null,
      countryCode: form.countryCode,
      timezone: 'Asia/Kolkata',
      startAt: form.startDate || null,
      endAt: form.endDate || null,
      callingWindowStart: convertTo24Hour(form.windowFromHour, form.windowFromMin, form.windowFromAmPm),
      callingWindowEnd: convertTo24Hour(form.windowToHour, form.windowToMin, form.windowToAmPm),
      maxConcurrent: 5,
      retryCount: parseInt(form.retryCount, 10),
      cooldownMinutes: parseInt(form.cooldown, 10),
      maxCallsPerHour: form.maxCallsPerHour ? parseInt(form.maxCallsPerHour, 10) : null,
      maxCallsPerDay: form.maxCallsPerDay ? parseInt(form.maxCallsPerDay, 10) : null,
      notificationEmails: form.notifEmails?.length > 0 ? form.notifEmails : null,
    };
  };

  // ─── Save Draft ─────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!form.campaignName?.trim() || !form.assistant) {
      setError('Campaign name and AI assistant are required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const payload = buildPayload();
      if (savedCampaignId) {
        // Already saved — no update endpoint yet, just show success
        setSaving(false);
        return;
      }
      const res = await api.post('/api/v1/campaigns', payload);
      const campaign = res.data?.data;
      setSavedCampaignId(campaign?.id);
    } catch (err) {
      console.error('Save draft failed:', err);
      setError(err?.response?.data?.message || 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Step 1 readiness check ────────────────────────────────────────────
  const isStep1Ready = (() => {
    if (!form.campaignName?.trim()) return false;
    if (!form.campaignType) return false;
    if (!form.assistant) return false;
    const sourceType = callingListTab === 'upload' ? 'csv_upload' : 'audience';
    if (sourceType === 'csv_upload' && !fileKey) return false;
    if (sourceType === 'audience' && !form.selectedAudience) return false;
    return true;
  })();

  // ─── Launch readiness check ─────────────────────────────────────────────
  const isLaunchReady = (() => {
    if (!isStep1Ready) return false;
    if (!form.callingNumber?.trim()) return false;
    if (!form.startDate || !form.endDate) return false;
    if (new Date(form.startDate) >= new Date(form.endDate)) return false;
    return true;
  })();

  // ─── Launch Campaign ────────────────────────────────────────────────────
  const handleLaunch = async () => {
    if (!form.campaignName?.trim() || !form.assistant) {
      setError('Campaign name and AI assistant are required.');
      return;
    }

    const sourceType = callingListTab === 'upload' ? 'csv_upload' : 'audience';
    if (sourceType === 'csv_upload' && !fileKey) {
      setError('Please upload a CSV file first.');
      return;
    }
    if (sourceType === 'audience' && !form.selectedAudience) {
      setError('Please select an audience.');
      return;
    }
    if (!form.startDate || !form.endDate) {
      setError('Campaign start date and end date are required.');
      return;
    }
    if (new Date(form.startDate) >= new Date(form.endDate)) {
      setError('End date must be after start date.');
      return;
    }

    setError('');
    setLaunching(true);
    try {
      let campaignId = savedCampaignId;

      // Create campaign if not already saved
      if (!campaignId) {
        const payload = buildPayload();
        const res = await api.post('/api/v1/campaigns', payload);
        campaignId = res.data?.data?.id;
        setSavedCampaignId(campaignId);
      }

      // Launch (backend handles CSV processing and activation)
      await api.post(`/api/v1/campaigns/${campaignId}/launch`);
      onClose?.();
    } catch (err) {
      console.error('Launch failed:', err);
      setError(err?.response?.data?.message || 'Failed to launch campaign.');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div className="fixed top-0 right-0 bottom-0 left-0 lg:left-[280px] z-50 bg-[#F8FAFC] overflow-y-auto">
      {/* Top Bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-[#E5E7EB] shadow-sm">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Campaign Setup</h1>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-6 py-8">
        {/* Step Indicator */}
        <StepIndicator currentStep={step} />

        {/* Error Banner */}
        {error && (
          <div className="mb-6 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
            {error}
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {uploadError && (
          <div className="mb-6 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
            {uploadError}
            <button onClick={() => setUploadError('')} className="ml-auto text-red-400 hover:text-red-600">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {/* Two-column layout */}
        <div className="flex gap-6 items-start">
          {/* Left Panel — 38% */}
          <div className="w-[38%] flex-shrink-0">
            {step === 1 ? (
              <Step1Panel
                form={form}
                setForm={setForm}
                uploadedFile={uploadedFile}
                setUploadedFile={handleSetUploadedFile}
                isDragging={isDragging}
                setIsDragging={setIsDragging}
                fileInputRef={fileInputRef}
                agents={agents}
                loadingAgents={loadingAgents}
                audiences={audiences}
                loadingAudiences={loadingAudiences}
                csvPreview={csvPreview}
                callingListTab={callingListTab}
                setCallingListTab={setCallingListTab}
                mappings={mappings}
                setMappings={setMappings}
                uploading={uploading}
              />
            ) : (
              <Step2Panel
                form={form}
                setForm={setForm}
                phoneNumbers={phoneNumbers}
                loadingNumbers={loadingNumbers}
                onBuyNumber={() => setShowBuyNumberModal(true)}
              />
            )}
          </div>

          {/* Right Panel — 62% */}
          <div className="flex-1">
            <CSVPreviewPanel
              uploadedFile={callingListTab === 'upload' ? uploadedFile : null}
              csvPreview={csvPreview}
              onChangeFile={handleChangeFile}
            />
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar */}
      <div className="sticky bottom-0 z-40 bg-white border-t border-[#E5E7EB] shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between">
          {/* Left: Save indicator */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {savedCampaignId ? (
              <>
                <Icon name="CheckCircle" size={15} className="text-green-500" />
                <span>Draft saved</span>
              </>
            ) : (
              <span className="text-gray-400">Not saved yet</span>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            {step === 1 ? (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-[#E5E7EB] rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!isStep1Ready}
                  className="px-5 py-2.5 text-sm font-semibold bg-[#2F55FF] text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue to Configure Campaign
                  <Icon name="ArrowRight" size={16} />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors flex items-center gap-2"
                >
                  <Icon name="ArrowLeft" size={15} />
                  Back
                </button>
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-[#E5E7EB] rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  onClick={handleLaunch}
                  disabled={launching || !isLaunchReady}
                  className="px-5 py-2.5 text-sm font-semibold bg-[#2F55FF] text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {launching ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Launching...
                    </>
                  ) : (
                    <>
                      <Icon name="Rocket" size={16} />
                      Launch Campaign
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Buy Number Modal */}
      {showBuyNumberModal && (
        <BuyNumberModal
          onClose={() => setShowBuyNumberModal(false)}
          onSuccess={() => {
            setShowBuyNumberModal(false);
            fetchPhoneNumbers();
          }}
        />
      )}
    </div>
  );
};

export default CampaignCreationWizard;
